/**
 * Scroll position across in-app navigation (ADR 0042 §"Perceived speed").
 *
 * The app is one document with a hash router, and its pages scroll the window. Left to the browser:
 *  - opening another page kept the old page's scroll offset (a new page opened half-way down);
 *  - going Back restored the offset before React had rendered the page, so it was clamped to the
 *    top and the person had to find their place in the list again.
 *
 * Here: a NEW page starts at the top; Back / Forward returns to where that history entry was left,
 * as soon as the page is tall enough again (lists are painted from the client cache at once, so
 * that is normally the next frame; it keeps trying for a moment while data arrives, and stops the
 * instant the person scrolls themselves). A change of query only (tabs, filters, an opened
 * preview) never moves the page.
 *
 * Every history entry gets a number in `history.state` (`xcNav`); positions are kept per number,
 * in memory. Entries whose state another part of the app replaces simply get a new number.
 */

const KEY = 'xcNav';
const positions = new Map<number, number>();
let counter = 0;
let current = 0;
let pending = 0; // id of the restore loop in progress

const pathOf = (hash: string) => (hash.replace(/^#/, '') || '/').split('?')[0];

function entryId(): number | null {
  const s = window.history.state as { [KEY]?: unknown } | null;
  return s && typeof s[KEY] === 'number' ? (s[KEY] as number) : null;
}

function stamp(): number {
  const id = ++counter;
  try {
    window.history.replaceState({ ...(window.history.state as object | null), [KEY]: id }, '');
  } catch {
    /* a sandboxed frame: positions are simply not remembered */
  }
  return id;
}

/**
 * Put the window at `y` once the page is tall enough, and hold it there for a few frames (the
 * page being left is still in the document for a moment, and a list may shrink to its skeleton
 * and grow back). Gives up after `maxMs`, and at once when the person scrolls themselves.
 */
function restore(y: number, maxMs = 2500): void {
  const run = ++pending;
  const started = performance.now();
  const cancel = () => {
    if (pending === run) pending++;
  };
  const inputs = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
  for (const name of inputs) window.addEventListener(name, cancel, { passive: true });
  const stop = () => {
    for (const name of inputs) window.removeEventListener(name, cancel);
  };
  let held = 0;
  const step = () => {
    if (pending !== run || performance.now() - started > maxMs) return stop();
    const reachable = document.documentElement.scrollHeight - window.innerHeight;
    if (reachable >= y - 1) {
      if (Math.abs(window.scrollY - y) > 1) {
        window.scrollTo(0, y);
        held = 0;
      } else if (++held >= 20) return stop();
    } else held = 0;
    requestAnimationFrame(step);
  };
  // Two frames: the router has swapped the page by then.
  requestAnimationFrame(() => requestAnimationFrame(step));
}

/**
 * Install once, AFTER the router module has registered its leave guard (a cancelled navigation
 * stops propagation there and never reaches this listener).
 */
export function installScrollRestore(): void {
  if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) return;
  window.history.scrollRestoration = 'manual';
  current = entryId() ?? stamp();
  counter = Math.max(counter, current);
  let lastPath = pathOf(window.location.hash);

  window.addEventListener(
    'hashchange',
    () => {
      // The window still shows the page being left: remember where it was.
      positions.set(current, window.scrollY);
      const path = pathOf(window.location.hash);
      const samePage = path === lastPath;
      lastPath = path;
      const known = entryId();
      if (known !== null && known !== current) {
        // Back / Forward to an entry seen before.
        counter = Math.max(counter, known);
        current = known;
        const y = positions.get(known);
        if (!samePage) restore(y ?? 0);
        return;
      }
      if (known === null) current = stamp(); // a new entry (a link, navigate())
      pending++; // whatever was being restored belongs to another page now
      if (!samePage) window.scrollTo(0, 0);
    },
    { capture: true },
  );
}
